import React from "react";
import { Box, Stack } from "@mui/material";
import { ParlorDetailProps } from "../../../types";
import { ColorSystemProps } from "../../../constants";

export function HeroDataCard({
  defaultParlor,
  mainColor,
  useTableMarkup = false,
}: {
  defaultParlor: ParlorDetailProps;
  mainColor: ColorSystemProps;
  useTableMarkup?: boolean;
}) {
  const binsos = defaultParlor.binsos || [];
  const expected = (defaultParlor as any).expected_charge_sum || {};
  const parkCnt = Number(defaultParlor.amenities?.parkcnt) || 0;

  const minPy = binsos.length
    ? Math.min(...binsos.map((b) => b.pyeong || 0))
    : 0;
  const maxPy = binsos.length
    ? Math.max(...binsos.map((b) => b.pyeong || 0))
    : 0;
  const minCharge = binsos.length
    ? Math.min(...binsos.map((b) => b.charge || 0))
    : 0;
  const maxCharge = binsos.length
    ? Math.max(...binsos.map((b) => b.charge || 0))
    : 0;
  const mubinso = expected["무빈소"] || 0;
  const ilban = expected["일반장"] || 0;
  const man = (n: number) => Math.round(n / 10000);

  const rows: { label: string; value: string; sub?: string }[] = [
    { label: "주소", value: defaultParlor.address },
  ];
  if (binsos.length)
    rows.push({
      label: "빈소 구성",
      value: `${binsos.length}실`,
      sub: `${minPy}~${maxPy}평 규모`,
    });
  if (minCharge && maxCharge)
    rows.push({
      label: "1일 빈소료",
      value: `${man(minCharge)}만~${man(maxCharge)}만원`,
      sub: "호실 선택 기준",
    });
  if (mubinso && ilban)
    rows.push({
      label: "예상 장례 비용",
      value: `${man(mubinso)}만~${man(ilban)}만원`,
      sub: "무빈소 ~ 일반장",
    });
  if (parkCnt)
    rows.push({
      label: "주차면",
      value: `${parkCnt}대`,
      sub: parkCnt < 10 ? "공설 특성상 협소" : "전용 주차",
    });
  rows.push({ label: "운영 시간", value: "24시간", sub: "연중무휴" });

  return (
    <Box
      style={{
        background: "#fafafa",
        border: "1px solid #edeae9",
        borderRadius: "12px",
        padding: "24px 22px",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap="12px"
        style={{ paddingBottom: "14px", borderBottom: "1px solid #edeae9" }}
      >
        <Box
          style={{
            fontSize: "16px",
            fontWeight: 800,
            color: mainColor.c500,
            letterSpacing: "0.02em",
          }}
        >
          {defaultParlor?.companyname} 정보
        </Box>
        <Box style={{ fontSize: "11px", color: "#8f8f8f" }}>
          보건복지부 e하늘 공공 데이터 기반
        </Box>
      </Stack>

      {useTableMarkup ? (
        <Box
          component="table"
          sx={{
            width: "100%",
            marginTop: "16px",
            borderCollapse: "collapse",
            "& tr": {
              borderBottom: "1px solid #f0eeed",
            },
            "& tr:last-of-type": {
              borderBottom: "none",
            },
            "& th": {
              padding: "10px 0",
              verticalAlign: "top",
              textAlign: "left",
              fontSize: "11px",
              fontWeight: 600,
              color: "#8f8f8f",
              letterSpacing: "0.04em",
              width: "33%",
              whiteSpace: "nowrap",
            },
            "& td": {
              padding: "10px 0",
              verticalAlign: "top",
              fontSize: "15px",
              fontWeight: 700,
              color: "#252525",
              fontFeatureSettings: '"tnum" on',
              lineHeight: 1.35,
            },
          }}
        >
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <th scope="row">{row.label}</th>
                <td>
                  {row.value}
                  {row.sub && (
                    <Box
                      component="small"
                      style={{
                        display: "block",
                        marginTop: "2px",
                        fontSize: "11px",
                        fontWeight: 500,
                        color: "#8f8f8f",
                      }}
                    >
                      {row.sub}
                    </Box>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Box>
      ) : (
        <Stack component="dl" gap="14px" style={{ marginTop: "16px" }}>
          {rows.map((row, i) => (
            <Box key={i}>
              <Box
                component="dt"
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#8f8f8f",
                  letterSpacing: "0.04em",
                }}
              >
                {row.label}
              </Box>
              <Box
                component="dd"
                style={{
                  marginTop: "3px",
                  fontSize: "15px",
                  fontWeight: 700,
                  color: "#252525",
                  fontFeatureSettings: '"tnum" on',
                  lineHeight: 1.35,
                }}
              >
                {row.value}
                {row.sub && (
                  <Box
                    component="small"
                    style={{
                      display: "block",
                      marginTop: "2px",
                      fontSize: "11px",
                      fontWeight: 500,
                      color: "#8f8f8f",
                    }}
                  >
                    {row.sub}
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Stack>
      )}

      <Box
        style={{
          marginTop: "18px",
          paddingTop: "14px",
          borderTop: "1px solid #edeae9",
          fontSize: "11px",
          color: "#686868",
          lineHeight: 1.6,
        }}
      >
        e하늘 장사 정보 시스템 기반으로 주기적으로 업데이트되고 있습니다.
      </Box>
    </Box>
  );
}
